import { useCallback, useMemo } from 'react';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { Gwei } from '../lib/gwei';
import { divide } from '../lib/math';
import { emptyObject, valueOrFallback } from '../lib/objectHelpers';
import { MarketPrice } from '../types/prices';
import { useMarketPrice } from './useWebsocketStore';

export const transformPriceUpdate = (price: MarketPrice) => {
  const { bestBidPrice, bestAskPrice, oraclePrice, price24hAgo } = price;

  const midPrice =
    bestBidPrice && bestAskPrice ? divide(bestBidPrice + bestAskPrice, Gwei.toGwei(2)) : undefined;

  const change24hrPrice = oraclePrice && price24hAgo ? oraclePrice - price24hAgo : undefined;
  const change24hrPercent =
    oraclePrice && change24hrPrice ? divide(change24hrPrice, oraclePrice) : undefined;

  return {
    productId: price.productId,
    bestAskPrice,
    bestBidPrice,
    oraclePrice,
    midPrice,
    change24hrPrice,
    change24hrPercent,
  };
};

export type ProductPrice = ReturnType<typeof transformPriceUpdate>;
export type PricesByProductId = Record<string, ProductPrice | undefined>;
type PricesState = {
  pricesByProductId: PricesByProductId;
  pendingPricesByProductId: PricesByProductId;
  lastUpdated: Date | null;

  setPrice: (productId: string, price: ProductPrice) => void;
  setPricesByProductId: () => void;
};

export const usePricesStore = create<PricesState>((set) => ({
  lastUpdated: null,
  pricesByProductId: {},
  pendingPricesByProductId: {},

  setPrice: (productId, price) =>
    set((state) => ({
      pendingPricesByProductId: { ...state.pendingPricesByProductId, [productId]: price },
    })),
  setPricesByProductId: () =>
    set((state) => {
      let merged: PricesByProductId = { ...state.pricesByProductId };

      for (const [productId, newPrice] of Object.entries(state.pendingPricesByProductId)) {
        const oldPrice = state.pricesByProductId[productId];

        const filteredPrice: ProductPrice = {
          productId,
          bestAskPrice: valueOrFallback(newPrice?.bestAskPrice, oldPrice?.bestAskPrice),
          bestBidPrice: valueOrFallback(newPrice?.bestBidPrice, oldPrice?.bestBidPrice),
          oraclePrice: valueOrFallback(newPrice?.oraclePrice, oldPrice?.oraclePrice),
          midPrice: valueOrFallback(newPrice?.midPrice, oldPrice?.midPrice),
          change24hrPrice: valueOrFallback(newPrice?.change24hrPrice, oldPrice?.change24hrPrice),
          change24hrPercent: valueOrFallback(
            newPrice?.change24hrPercent,
            oldPrice?.change24hrPercent
          ),
        };

        merged[productId] = filteredPrice;
      }

      return {
        lastUpdated: new Date(),
        pendingPricesByProductId: emptyObject,
        pricesByProductId: merged,
      };
    }),
}));

// Hook for subscribing to a single product price
export const useProductPriceSubSetup = (productId: string | undefined) => {
  const { setPrice } = usePricesStore();

  const handlePriceUpdate = useCallback(
    (priceUpdate: MarketPrice) => {
      setPrice(priceUpdate.productId, transformPriceUpdate(priceUpdate));
    },
    [setPrice]
  );

  useMarketPrice(productId, handlePriceUpdate);
};

export const useProductPricesSub = (): {
  data: PricesByProductId;
  dataUpdatedAt: Date | null;
  isPending: boolean;
} => {
  const lastUpdated = usePricesStore((state) => state.lastUpdated);
  const data = usePricesStore(useShallow((state) => state.pricesByProductId));

  return useMemo(
    () => ({ data, isPending: !lastUpdated, dataUpdatedAt: lastUpdated }),
    [data, lastUpdated]
  );
};
